import React, { useEffect, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import heartLoading from "../../../assests/heartLoading.gif";
import KpiLineage from "./KpiLineage";

export default function KpiLineageFullScreenPage() {
  const [searchParams] = useSearchParams();
  const [lineageData, setLineageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get parameters
  const mode = searchParams.get("mode") || "asset"; // asset, product, or glossary
  const assetName = searchParams.get("assetName") || "";
  const productName = searchParams.get("productName") || "";
  const kpiName = searchParams.get("kpiName") || "";

  useEffect(() => {
    setLoading(true);
    setError(null);
    setLineageData(null);

    const fetchData = async () => {
      try {
        let response;

        if (mode === "asset" && assetName) {
          response = await axios.get(
            `${process.env.REACT_APP_API_URL}/kpi-lineage`,
            { params: { asset_name: assetName } }
          );
          setLineageData(response.data);
        } else if (mode === "product" && productName) {
          response = await axios.get(
            `${process.env.REACT_APP_API_URL}/kpi-product`,
            { params: { product_name: productName } }
          );
          setLineageData(response.data);
        } else if (mode === "glossary" && kpiName) {
          response = await axios.get(
            `${process.env.REACT_APP_API_URL}/kpi-all-products`,
            { params: { kpi_name: kpiName } }
          );
          setLineageData({ products: response.data?.products });
        }
      } catch (err) {
        setError("Failed to load KPI lineage data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (
      (mode === "asset" && assetName) ||
      (mode === "product" && productName) ||
      (mode === "glossary" && kpiName)
    ) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [mode, assetName, productName, kpiName]);

  const getTitle = () => {
    if (mode === "asset") return assetName || "Asset KPI Lineage";
    if (mode === "product") return productName || "Product KPI Lineage";
    if (mode === "glossary") return kpiName || "KPI Lineage";
    return "KPI Lineage";
  };

  const getModeLabel = () => {
    if (mode === "asset") return "Asset KPI Lineage";
    if (mode === "product") return "Product KPI Lineage";
    if (mode === "glossary") return "Glossary KPI Lineage";
    return "KPI Lineage";
  };

  const getLineageMode = () => {
    if (mode === "glossary") return "product-asset-column";
    return "kpi"; // default for asset and product
  };

  return (
    <div className="h-screen bg-slate-100">
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-6 py-2 shadow-sm">
          <button
            onClick={() => window.history.back()}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Go back"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              {getModeLabel()}
            </p>
            <h1 className="truncate text-sm font-semibold text-slate-900">
              {getTitle()}
            </h1>
          </div>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 p-4 overflow-hidden">
          {loading ? (
            <div className="flex h-full items-center justify-center rounded-2xl bg-white shadow-sm">
              <div className="flex flex-col items-center justify-center space-y-3">
                <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
                <span className="text-gray-700 text-[20px]">
                  Loading KPI lineage...
                </span>
              </div>
            </div>
          ) : error ? (
            <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white text-center">
              <div className="space-y-2 px-6">
                <p className="text-lg font-semibold text-red-600">{error}</p>
                <p className="text-sm text-slate-500">
                  Please try again or go back to the previous page.
                </p>
              </div>
            </div>
          ) : !lineageData ? (
            <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white text-center">
              <div className="space-y-2 px-6">
                <p className="text-lg font-semibold text-slate-800">
                  No data available
                </p>
                <p className="text-sm text-slate-500">
                  No KPI lineage data found for the selected {mode}.
                </p>
              </div>
            </div>
          ) : (
            <div className="h-full overflow-hidden rounded-2xl shadow-sm bg-white">
              <KpiLineage
                data={lineageData}
                loading={false}
                assetName={mode === "asset" ? assetName : undefined}
                lineageMode={getLineageMode()}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
